/**
 * Accessibility utilities for ensuring WCAG compliance
 */

/**
 * Calculate relative luminance of a color
 * Based on WCAG 2.1 formula
 */
export const getRelativeLuminance = (rgb: {
  r: number;
  g: number;
  b: number;
}): number => {
  const { r, g, b } = rgb;

  // Convert to sRGB
  const rsRGB = r / 255;
  const gsRGB = g / 255;
  const bsRGB = b / 255;

  // Apply gamma correction
  const rLinear =
    rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const gLinear =
    gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const bLinear =
    bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  // Calculate relative luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Parse hex color to RGB
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

/**
 * Calculate contrast ratio between two colors
 * Returns a value between 1 and 21
 */
export const getContrastRatio = (color1: string, color2: string): number => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);

  if (!rgb1 || !rgb2) {
    console.warn("Invalid color format. Use hex colors (e.g., #ffffff)");
    return 1;
  }

  const l1 = getRelativeLuminance(rgb1);
  const l2 = getRelativeLuminance(rgb2);

  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Check if contrast ratio meets WCAG AA standards
 * AA requires 4.5:1 for normal text, 3:1 for large text
 */
export const meetsWCAG_AA = (
  foreground: string,
  background: string,
  isLargeText = false
): boolean => {
  const ratio = getContrastRatio(foreground, background);
  const threshold = isLargeText ? 3 : 4.5;
  return ratio >= threshold;
};

/**
 * Check if contrast ratio meets WCAG AAA standards
 * AAA requires 7:1 for normal text, 4.5:1 for large text
 */
export const meetsWCAG_AAA = (
  foreground: string,
  background: string,
  isLargeText = false
): boolean => {
  const ratio = getContrastRatio(foreground, background);
  const threshold = isLargeText ? 4.5 : 7;
  return ratio >= threshold;
};

/**
 * Get accessibility level for a color combination
 */
export const getAccessibilityLevel = (
  foreground: string,
  background: string,
  isLargeText = false
): "AAA" | "AA" | "Fail" => {
  if (meetsWCAG_AAA(foreground, background, isLargeText)) {
    return "AAA";
  } else if (meetsWCAG_AA(foreground, background, isLargeText)) {
    return "AA";
  }
  return "Fail";
};

/**
 * Validate color contrast and log warnings in development
 */
export const validateColorContrast = (
  foreground: string,
  background: string,
  elementDescription: string,
  isLargeText = false
): void => {
  if (process.env.NODE_ENV === "development") {
    const ratio = getContrastRatio(foreground, background);
    const level = getAccessibilityLevel(foreground, background, isLargeText);

    if (level === "Fail") {
      console.warn(
        `⚠️ Accessibility Warning: ${elementDescription}`,
        `\nContrast ratio: ${ratio.toFixed(2)}:1`,
        `\nRequired: ${isLargeText ? "3:1" : "4.5:1"} (WCAG AA)`,
        `\nForeground: ${foreground}`,
        `\nBackground: ${background}`
      );
    } else {
      console.log(
        `✅ Accessibility Check: ${elementDescription}`,
        `\nContrast ratio: ${ratio.toFixed(2)}:1`,
        `\nLevel: WCAG ${level}`
      );
    }
  }
};

/**
 * Check if user prefers reduced motion
 */
export const prefersReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

/**
 * Check if user prefers high contrast
 */
export const prefersHighContrast = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(prefers-contrast: high)").matches ||
    window.matchMedia("(prefers-contrast: more)").matches
  );
};

/**
 * Get focus visible styles for keyboard navigation
 */
export const getFocusVisibleStyles = (color = "#a53c49") => ({
  "&:focus-visible": {
    outline: `2px solid ${color}`,
    outlineOffset: "3px",
    borderRadius: "4px",
  },
});

/**
 * Skip to main content link for keyboard navigation
 */
export const createSkipLink = (): HTMLAnchorElement => {
  const skipLink = document.createElement("a");
  skipLink.href = "#main-content";
  skipLink.textContent = "Skip to main content";
  skipLink.className = "skip-link";
  skipLink.style.cssText = `
    position: absolute;
    top: -40px;
    left: 0;
    background: #a53c49;
    color: white;
    padding: 8px 16px;
    text-decoration: none;
    z-index: 10000;
    border-radius: 0 0 4px 0;
    font-weight: 600;
  `;

  // Show on focus
  skipLink.addEventListener("focus", () => {
    skipLink.style.top = "0";
  });

  skipLink.addEventListener("blur", () => {
    skipLink.style.top = "-40px";
  });

  return skipLink;
};

/**
 * Initialize accessibility features
 */
export const initializeAccessibility = (): void => {
  if (typeof window === "undefined") return;

  // Add skip link
  const skipLink = createSkipLink();
  document.body.insertBefore(skipLink, document.body.firstChild);

  // Add main content ID if not present
  const main = document.querySelector("main");
  if (main && !main.id) {
    main.id = "main-content";
  }

  // Log accessibility preferences
  if (process.env.NODE_ENV === "development") {
    console.log("Accessibility Preferences:", {
      reducedMotion: prefersReducedMotion(),
      highContrast: prefersHighContrast(),
    });
  }
};

export default {
  getContrastRatio,
  meetsWCAG_AA,
  meetsWCAG_AAA,
  getAccessibilityLevel,
  validateColorContrast,
  prefersReducedMotion,
  prefersHighContrast,
  getFocusVisibleStyles,
  initializeAccessibility,
};
